/**
 * @file languages.ts
 * @brief Language code helpers for Meeting Intel webpanel (mobile parity)
 *
 * @details
 * ISO base codes, English display names, and RTL detection for MoM language
 * pills. Mirrors FRONTEND/src/shared/data/languages.ts + textDirection helpers
 * without pulling the full mobile language catalog.
 *
 * @author DES
 * @date 13-07-2026
 */

const RTL_LANG_BASES = new Set([
  "ar",
  "he",
  "iw",
  "fa",
  "ur",
  "ps",
  "ku",
  "sd",
  "ug",
  "yi",
  "dv",
]);

const ENGLISH_NAMES: Record<string, string> = {
  en: "English",
  ur: "Urdu",
  ar: "Arabic",
  hi: "Hindi",
  fa: "Persian",
  he: "Hebrew",
  ps: "Pashto",
  bn: "Bengali",
  pa: "Punjabi",
  es: "Spanish",
  fr: "French",
  de: "German",
  zh: "Chinese",
  ja: "Japanese",
  ko: "Korean",
  tr: "Turkish",
  pt: "Portuguese",
  ru: "Russian",
};

/** ISO 639-1 base from `ur-pk` / `UR_PK` / `ur`. */
export function languageBaseCode(
  identifier?: string | null,
): string {
  if (!identifier?.trim()) return "";
  return identifier.trim().toLowerCase().split(/[-_]/)[0] ?? "";
}

export function isRtlLanguageCode(languageCode?: string | null): boolean {
  const base = languageBaseCode(languageCode);
  return !!base && RTL_LANG_BASES.has(base);
}

export function getLanguageEnglishName(
  identifier?: string | null,
  fallback?: string,
): string {
  const base = languageBaseCode(identifier);
  if (base && ENGLISH_NAMES[base]) return ENGLISH_NAMES[base];
  if (fallback?.trim()) return fallback.trim();
  return (identifier || "Language").toUpperCase();
}

export function sameLanguageBase(
  a?: string | null,
  b?: string | null,
): boolean {
  const ba = languageBaseCode(a);
  const bb = languageBaseCode(b);
  return !!ba && ba === bb;
}

/** Mobile Meeting Intel transcript-convert dialect trio (exact labels). */
const MOBILE_TRANSCRIPT_DIALECTS: Array<{
  code: string;
  locale: string;
  label: string;
}> = [
  { code: "ur", locale: "ur-PK", label: "Urdu (Nastaliq)" },
  { code: "hi", locale: "hi-IN", label: "Hindi (Devanagari)" },
  { code: "en", locale: "en-GB", label: "English (UK)" },
];

const DIALECT_LOCALES: Record<string, string> = {
  ur: "ur-PK",
  hi: "hi-IN",
  en: "en-GB",
  ar: "ar-SA",
  es: "es-ES",
  fr: "fr-FR",
  de: "de-DE",
  pt: "pt-PT",
  zh: "zh-CN",
};

export type MeetingTranscriptExportLanguage = {
  code: string;
  label: string;
  locale?: string;
};

/**
 * Languages for webpanel "Export transcript PDF" picker — same candidates as
 * mobile Meeting Intel script-convert (`Urdu / Hindi / English` + meeting
 * target langs + participant native langs). (13-07-2026)
 */
export function buildMeetingTranscriptExportLanguages(input: {
  targetLanguages?: Array<string | null | undefined> | null;
  participantNativeLanguages?: Array<string | null | undefined> | null;
}): MeetingTranscriptExportLanguage[] {
  const out: MeetingTranscriptExportLanguage[] = [];
  const seen = new Set<string>();

  const push = (code: string, locale: string | undefined, label: string) => {
    const base = languageBaseCode(code);
    if (!base || seen.has(base)) return;
    seen.add(base);
    out.push({
      code: base,
      locale: locale || DIALECT_LOCALES[base] || base,
      label,
    });
  };

  for (const d of MOBILE_TRANSCRIPT_DIALECTS) {
    push(d.code, d.locale, d.label);
  }

  const extras = [
    ...(input.targetLanguages ?? []),
    ...(input.participantNativeLanguages ?? []),
  ];
  for (const raw of extras) {
    if (!raw?.trim()) continue;
    const base = languageBaseCode(raw);
    if (!base || seen.has(base)) continue;
    const dialect = MOBILE_TRANSCRIPT_DIALECTS.find((d) => d.code === base);
    push(
      base,
      dialect?.locale || DIALECT_LOCALES[base] || raw.trim(),
      dialect?.label || getLanguageEnglishName(raw),
    );
  }

  return out;
}
